import type { AgentDto, EnvVarDto, ImportEnvResult, Role } from '@agent-platform/shared';
import { hasRole } from '@agent-platform/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';
import { EmptyState } from '@/components/empty-state';
import { ErrorAlert } from '@/components/error-alert';
import { ListSearch, NoMatches, useListSearch } from '@/components/list-search';
import { PageHeader } from '@/components/page-header';
import { RequireTenant } from '@/components/require-tenant';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { api, errorMessage } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { usePaged } from '@/lib/queries';

const TENANT = 'tenant';

function ScopePicker({
  id,
  value,
  onChange,
  agents,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  agents: AgentDto[];
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger id={id} aria-label="Scope">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={TENANT}>All agents (tenant)</SelectItem>
        {agents.map((a) => (
          <SelectItem key={a.id} value={a.id}>
            Only {a.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const scopeBody = (scope: string) =>
  scope === TENANT ? { scope: 'tenant' } : { scope: 'agent', agentId: scope };

function AddVariableDialog({ tenantId, agents }: { tenantId: string; agents: AgentDto[] }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [isSecret, setIsSecret] = useState(false);
  const [scope, setScope] = useState(TENANT);
  const create = useMutation({
    mutationFn: () =>
      api<EnvVarDto>('/env-vars', {
        method: 'POST',
        body: { key, value, isSecret, ...scopeBody(scope) },
      }),
    onSuccess: (v) => {
      toast.success(`${v.key} saved`);
      void queryClient.invalidateQueries({ queryKey: ['env-vars', tenantId] });
      setOpen(false);
      setKey('');
      setValue('');
      setIsSecret(false);
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) create.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button>Add variable</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add an environment variable</DialogTitle>
          <DialogDescription>
            Use it as <code>{'{{env.KEY}}'}</code> in tools, in MCP headers and in request mappings.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          noValidate
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <ErrorAlert error={create.error} />
          <div className="grid gap-1.5">
            <Label htmlFor="env-key">Key</Label>
            <Input
              id="env-key"
              className="font-mono"
              autoComplete="off"
              value={key}
              onChange={(e) => setKey(e.target.value.toUpperCase())}
              placeholder="WEATHER_TOKEN"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="env-value">Value</Label>
            <Input
              id="env-value"
              type={isSecret ? 'password' : 'text'}
              autoComplete="new-password"
              data-1p-ignore
              data-lpignore="true"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isSecret}
              onChange={(e) => setIsSecret(e.target.checked)}
            />
            Secret — encrypted, never shown again, redacted from runs
          </label>
          <div className="grid gap-1.5">
            <Label htmlFor="env-scope">Scope</Label>
            <ScopePicker id="env-scope" value={scope} onChange={setScope} agents={agents} />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={create.isPending}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ImportDialog({ tenantId, agents }: { tenantId: string; agents: AgentDto[] }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState('');
  const [asSecrets, setAsSecrets] = useState(true);
  const [overwrite, setOverwrite] = useState(false);
  const [scope, setScope] = useState(TENANT);
  const run = useMutation({
    mutationFn: () =>
      api<ImportEnvResult>('/env-vars/import', {
        method: 'POST',
        body: { content, asSecrets, overwrite, ...scopeBody(scope) },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['env-vars', tenantId] }),
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          run.reset();
          setContent('');
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline">Import .env</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Import from .env</DialogTitle>
          <DialogDescription>
            Paste <code>KEY=value</code> lines. Comments, quotes and <code>export</code> are
            understood.
          </DialogDescription>
        </DialogHeader>
        <ErrorAlert error={run.error} />
        {run.data ? (
          <div className="grid gap-2 text-sm">
            <p>
              {run.data.created} created, {run.data.updated} updated.
            </p>
            {run.data.skipped.length > 0 && (
              <ul className="text-muted-foreground list-disc pl-5">
                {run.data.skipped.map((s) => (
                  <li key={`${s.line}`}>
                    Line {s.line}
                    {s.key ? ` (${s.key})` : ''}: {s.reason}
                  </li>
                ))}
              </ul>
            )}
            <DialogFooter>
              <Button onClick={() => setOpen(false)}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="grid gap-4">
            <Label htmlFor="env-import" className="sr-only">
              .env content
            </Label>
            <Textarea
              id="env-import"
              className="min-h-48 font-mono text-xs"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={'REGION=eu-west-1\nWEATHER_TOKEN="…"'}
            />
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={asSecrets}
                  onChange={(e) => setAsSecrets(e.target.checked)}
                />
                Store as secrets
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={overwrite}
                  onChange={(e) => setOverwrite(e.target.checked)}
                />
                Overwrite existing
              </label>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="import-scope">Scope</Label>
              <ScopePicker id="import-scope" value={scope} onChange={setScope} agents={agents} />
            </div>
            <DialogFooter>
              <Button onClick={() => run.mutate()} disabled={!content.trim() || run.isPending}>
                Import
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EnvironmentContent({ tenantId, role }: { tenantId: string; role: Role }) {
  const queryClient = useQueryClient();
  const canEdit = hasRole(role, 'admin');
  const vars = useQuery({
    queryKey: ['env-vars', tenantId],
    queryFn: () => api<{ items: EnvVarDto[] }>('/env-vars'),
  });
  const agents = usePaged<AgentDto>(['agents', tenantId], '/agents');
  const agentList = agents.data?.pages.flatMap((p) => p.items) ?? [];
  const [filter, setFilter] = useState('all');
  const remove = useMutation({
    mutationFn: (id: string) => api<void>(`/env-vars/${id}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['env-vars', tenantId] }),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const rows = (vars.data?.items ?? []).filter(
    (v) => filter === 'all' || (filter === TENANT ? v.scope === 'tenant' : v.agentId === filter),
  );
  const agentName = (id: string | null) =>
    agentList.find((a) => a.id === id)?.name ?? 'Deleted agent';
  // Key and scope only: values (secret or not) are never searched.
  const search = useListSearch(rows, (v) => [
    v.key,
    v.scope,
    v.scope === 'tenant' ? 'All agents' : agentName(v.agentId),
  ]);

  return (
    <>
      <PageHeader
        title="Environment"
        description="Variables and secrets for tools, MCP servers and request mappings. Agent-scoped values override tenant ones."
        actions={
          canEdit && (
            <>
              <ImportDialog tenantId={tenantId} agents={agentList} />
              <AddVariableDialog tenantId={tenantId} agents={agentList} />
            </>
          )
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <ListSearch
          className="w-full max-w-md"
          value={search.query}
          onChange={search.setQuery}
          label="Search variables"
          placeholder="Search by key, scope or agent"
        />
        <div>
          <Label htmlFor="env-filter" className="sr-only">
            Filter by scope
          </Label>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger id="env-filter" aria-label="Filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All scopes</SelectItem>
              <SelectItem value={TENANT}>Tenant</SelectItem>
              {agentList.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <ErrorAlert error={vars.error} />
      {!vars.isPending && rows.length === 0 ? (
        <EmptyState title="No variables">
          Add variables one by one or import a .env file.
        </EmptyState>
      ) : (
        <div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Key</TableHead>
                <TableHead>Value</TableHead>
                <TableHead>Scope</TableHead>
                <TableHead>Updated</TableHead>
                {canEdit && <TableHead className="sr-only">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {search.filtered.map((v) => (
                <TableRow key={v.id}>
                  <TableCell className="font-mono text-sm">{v.key}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {v.isSecret ? (
                      <span className="flex items-center gap-2">
                        <Badge variant="outline">Secret</Badge>••••
                        {v.last4 && v.last4 !== '••••' ? v.last4 : ''}
                      </span>
                    ) : (
                      v.value
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {v.scope === 'tenant' ? 'All agents' : agentName(v.agentId)}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatDateTime(v.updatedAt)}
                  </TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <Button
                        variant="ghost-destructive"
                        size="sm"
                        onClick={() => remove.mutate(v.id)}
                        aria-label={`Delete ${v.key}`}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {rows.length > 0 && search.filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={canEdit ? 5 : 4}>
                    <NoMatches query={search.query} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  );
}

export function EnvironmentPage() {
  return (
    <RequireTenant>
      {(tenant) => <EnvironmentContent tenantId={tenant.id} role={tenant.role} />}
    </RequireTenant>
  );
}
